import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, QrCode, Utensils, BarChart3, Bell, Palette, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/17086289/pexels-photo-17086289.jpeg";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1670819917685-f1040e76b9b7";
const MENU_IMG = "https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg";

const FEATURE_ICONS = [Utensils, QrCode, Bell, Palette, BarChart3, CreditCard];

export default function Landing() {
  const { user } = useAuth();
  const [plans, setPlans] = React.useState([]);

  React.useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-border/60">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-baseline gap-2" data-testid="brand-link">
            <span className="font-display text-2xl font-black">Tavolo<span className="text-[hsl(var(--accent))]">OS</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-[hsl(var(--accent))]" data-testid="nav-features">{t.nav.features}</a>
            <a href="#pricing" className="hover:text-[hsl(var(--accent))]" data-testid="nav-pricing">{t.nav.pricing}</a>
          </nav>
          <div className="flex items-center gap-3">
            {user && user.role ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"}>
                <Button className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90" data-testid="nav-dashboard-btn">
                  {t.nav.dashboard}
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm hover:text-[hsl(var(--accent))]" data-testid="nav-login">{t.nav.login}</Link>
                <Link to="/register">
                  <Button className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90" data-testid="nav-register-btn">
                    {t.nav.register}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-[hsl(var(--accent))] mb-6" data-testid="hero-kicker">
              {t.hero.kicker}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter">
              {t.hero.title1}<br />
              <span className="italic font-light text-[hsl(var(--accent))]">{t.hero.title2}</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="pill-btn bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))] hover:text-white px-8 h-12" data-testid="hero-cta-primary">
                  {t.hero.cta_primary} <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="pill-btn border-primary/30 hover:bg-primary hover:text-primary-foreground px-8 h-12" data-testid="hero-cta-secondary">
                  {t.hero.cta_secondary}
                </Button>
              </a>
            </div>
            <div className="mt-12 flex gap-8 text-xs uppercase tracking-widest text-muted-foreground">
              <div><span className="block text-3xl font-display text-foreground">180+</span> ristoranti</div>
              <div><span className="block text-3xl font-display text-foreground">4.9★</span> soddisfazione</div>
              <div><span className="block text-3xl font-display text-foreground">0%</span> commissioni</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="grid grid-cols-6 grid-rows-6 gap-4 h-[520px]">
              <div className="col-span-6 row-span-4 rounded-2xl overflow-hidden tetris-shadow relative">
                <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 noise-overlay opacity-40" />
              </div>
              <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden tetris-shadow">
                <img src={INTERIOR_IMG} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-3 row-span-2 rounded-2xl bg-primary text-primary-foreground p-6 flex flex-col justify-between">
                <QrCode className="h-8 w-8" />
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">Scan &amp; order</div>
                  <div className="font-display text-2xl mt-1">Tavolo 12</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <h2 className="lg:col-span-7 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            {t.features.title}
          </h2>
          <p className="lg:col-span-5 text-muted-foreground text-lg">
            Uno strumento pensato per chi lavora in sala. Semplice per lo staff, elegante per i clienti.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger">
          {t.features.items.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Utensils;
            return (
              <div key={i} className="p-8 bg-card border border-border rounded-2xl hover:border-[hsl(var(--accent))] transition-colors" data-testid={`feature-card-${i}`}>
                <Icon className="h-6 w-6 text-[hsl(var(--accent))] mb-6" />
                <h3 className="font-display text-2xl font-bold tracking-tight">{f.t}</h3>
                <p className="mt-3 text-muted-foreground">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[hsl(var(--secondary))] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">{t.pricing.title}</h2>
            <p className="mt-4 text-muted-foreground">{t.pricing.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p, idx) => {
              const popular = idx === 1;
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.lookup_key}`}
                  className={`relative p-8 rounded-2xl bg-card border ${popular ? "border-[hsl(var(--accent))] shadow-xl" : "border-border"}`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-6 bg-[hsl(var(--accent))] text-white text-xs uppercase tracking-widest px-3 py-1 rounded-full">
                      {t.pricing.most_popular}
                    </span>
                  )}
                  <div className="font-display text-2xl font-bold">{p.name}</div>
                  <div className="mt-4 font-display text-5xl font-black tracking-tighter">
                    €{(p.price_cents / 100).toFixed(0)}
                    <span className="text-base font-normal text-muted-foreground">{t.pricing.per_month}</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" className="block mt-8">
                    <Button
                      className={`w-full pill-btn ${popular ? "bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                      data-testid={`plan-cta-${p.lookup_key}`}
                    >
                      {t.pricing.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 py-12 border-t border-border">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="font-display text-xl font-black">Tavolo<span className="text-[hsl(var(--accent))]">OS</span></div>
          <div className="text-sm text-muted-foreground">© {new Date().getFullYear()} TavoloOS — Fatto con cura in Italia.</div>
        </div>
      </footer>
    </div>
  );
}
